import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MagnifyingGlassIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import GroupManager, { type RosterGroup } from './GroupManager'
import AthleteSlideout from './AthleteSlideout'

// ─── Types ───────────────────────────────────────────────────────────────

export type LockerRoomMember = {
  id?: string | number | null
  locker_room_id?: string | null
  prospect_id: number
  role?: string | null
  active?: boolean | null
  status?: string | null
  prospect?: {
    id: number
    name?: string | null
    avatar_url?: string | null
    headline?: string | null
    status?: string | null
    athletic_profile?: Record<string, unknown> | null
  } | null
}

type LockerRoomProspect = NonNullable<LockerRoomMember['prospect']>

export type ProspectBudgetPlan = {
  prospect_id: number
  remaining_cents?: number | null
  annual_limit_cents?: number | null
  monthly_limit_cents?: number | null
  soft_monthly_limit_cents?: number | null
  planned_annual_allocation_cents?: number | null
}

export type ProspectActivity = {
  lastSession: {
    id?: string | number
    partner?: string | null
    status?: string | null
    metadata?: Record<string, unknown> | null
    started_at?: string | null
    updated_at?: string | null
  } | null
  monthlyCount: number
  totalCount: number
}

type SortField = 'name' | 'status' | 'sport' | 'team' | 'classYear'
type SortDir = 'asc' | 'desc'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'eligible', label: 'Eligible', color: 'bg-blue-100 text-blue-800' },
  { value: 'injured', label: 'Injured', color: 'bg-amber-100 text-amber-800' },
  { value: 'graduated', label: 'Graduated', color: 'bg-purple-100 text-purple-800' },
  { value: 'transferred', label: 'Transferred', color: 'bg-gray-200 text-gray-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-gray-200 text-gray-600' },
]

function getStatusMeta(status: string) {
  return STATUS_OPTIONS.find((s) => s.value === status) || { value: status, label: status, color: 'bg-gray-100 text-gray-600' }
}

interface RosterManagerProps {
  roster: LockerRoomMember[]
  rosterLoading: boolean
  budgetPlans: Record<number, ProspectBudgetPlan>
  activityByProspect: Record<number, ProspectActivity>
  linkableProspects: LockerRoomProspect[]
  groups: RosterGroup[]
  groupMemberships: Record<number, string[]>  // prospectId → groupIds
  token: string
  onToggleActive: (member: LockerRoomMember, nextActive: boolean) => Promise<void>
  onStatusChange: (prospectId: number, status: string) => Promise<void>
  onLinkProspect: (prospectId: number) => Promise<void>
  onRemoveMember: (member: LockerRoomMember) => Promise<void>
  onBulkStatus: (prospectIds: number[], status: string) => Promise<void>
  onGroupCreate: (name: string, color: string) => Promise<void>
  onGroupUpdate: (groupId: string, name: string, color: string) => Promise<void>
  onGroupDelete: (groupId: string) => Promise<void>
  onGroupToggle: (groupId: string, prospectId: number, add: boolean) => Promise<void>
  onBulkGroupAssign: (groupId: string, prospectIds: number[]) => Promise<void>
  onNominateProspect?: (data: { name: string; email?: string; sport?: string }) => Promise<void>
  onAddByUuid?: (uuid: string) => Promise<void>
  onRefresh: () => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatCurrency(cents?: number | null) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format((cents as number) / 100)
}

function prospectName(prospect?: LockerRoomMember['prospect'], id?: number) {
  if (!prospect && id) return `Prospect #${id}`
  if (!prospect) return 'Prospect'
  return prospect.name || prospect.headline || `Prospect #${prospect.id}`
}

function getSports(member: LockerRoomMember): string[] {
  const ap = member.prospect?.athletic_profile as Record<string, unknown> | null
  return Array.isArray(ap?.sports) ? (ap.sports as string[]) : []
}

function getTeam(member: LockerRoomMember): string {
  const ap = member.prospect?.athletic_profile as Record<string, unknown> | null
  return (ap?.team as string) || ''
}

function getClassYear(member: LockerRoomMember): number | null {
  const ap = member.prospect?.athletic_profile as Record<string, unknown> | null
  return typeof ap?.class_year === 'number' ? ap.class_year : null
}

function getMemberStatus(member: LockerRoomMember): string {
  return member.status || (member.active === false ? 'inactive' : 'active')
}

// ─── Component ───────────────────────────────────────────────────────────

export default function RosterManager(props: RosterManagerProps) {
  const {
    roster, rosterLoading, budgetPlans, activityByProspect, linkableProspects,
    groups, groupMemberships, token,
    onToggleActive, onStatusChange, onLinkProspect, onRemoveMember, onBulkStatus,
    onGroupCreate, onGroupUpdate, onGroupDelete, onGroupToggle, onBulkGroupAssign,
    onNominateProspect, onAddByUuid, onRefresh,
  } = props

  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sportFilter, setSportFilter] = useState('')
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [linkDraft, setLinkDraft] = useState('')
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  // Nominate form
  const [showNominate, setShowNominate] = useState(false)
  const [nomName, setNomName] = useState('')
  const [nomEmail, setNomEmail] = useState('')
  const [nomSport, setNomSport] = useState('')
  const [nomSubmitting, setNomSubmitting] = useState(false)
  const [nomMessage, setNomMessage] = useState('')
  // UUID add (subtle)
  const [showUuid, setShowUuid] = useState(false)
  const [uuidInput, setUuidInput] = useState('')
  const [uuidLoading, setUuidLoading] = useState(false)
  const [viewingMember, setViewingMember] = useState<LockerRoomMember | null>(null)
  const [bulkStatusValue, setBulkStatusValue] = useState('active')
  const [bulkGroupId, setBulkGroupId] = useState('')

  // Collect all sports across roster for filter dropdown
  const allSports = useMemo(() => {
    const s = new Set<string>()
    for (const m of roster) for (const sp of getSports(m)) s.add(sp)
    return Array.from(s).sort()
  }, [roster])

  // Filter + sort
  const filtered = useMemo(() => {
    let list = [...roster]

    if (statusFilter !== 'all') list = list.filter((m) => getMemberStatus(m) === statusFilter)
    if (sportFilter) list = list.filter((m) => getSports(m).includes(sportFilter))
    if (activeGroupId) {
      list = list.filter((m) => (groupMemberships[m.prospect_id] || []).includes(activeGroupId))
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((m) => prospectName(m.prospect, m.prospect_id).toLowerCase().includes(q))
    }

    list.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'name':
          cmp = prospectName(a.prospect, a.prospect_id).localeCompare(prospectName(b.prospect, b.prospect_id))
          break
        case 'status':
          cmp = getMemberStatus(a).localeCompare(getMemberStatus(b))
          break
        case 'sport':
          cmp = getSports(a).join(',').localeCompare(getSports(b).join(','))
          break
        case 'team':
          cmp = getTeam(a).localeCompare(getTeam(b))
          break
        case 'classYear':
          cmp = (getClassYear(a) || 9999) - (getClassYear(b) || 9999)
          break
      }
      return sortDir === 'desc' ? -cmp : cmp
    })

    return list
  }, [roster, statusFilter, sportFilter, activeGroupId, search, sortField, sortDir, groupMemberships])

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir('asc') }
  }
  const sortInd = (field: SortField) => sortField !== field ? '' : sortDir === 'asc' ? ' ↑' : ' ↓'

  const allSelected = filtered.length > 0 && filtered.every((m) => selected.has(m.prospect_id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((m) => m.prospect_id)))
  const toggleOne = (id: number) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const activeCount = roster.filter((m) => getMemberStatus(m) === 'active' || getMemberStatus(m) === 'eligible').length

  // CSV export
  const exportCsv = useCallback(() => {
    const headers = ['Name', 'Status', 'Sports', 'Position', 'Team', 'Class Year', 'Height', 'Weight', 'Location']
    const rows = roster.map((m) => {
      const ap = (m.prospect?.athletic_profile || {}) as Record<string, unknown>
      return [
        prospectName(m.prospect, m.prospect_id),
        getMemberStatus(m),
        (Array.isArray(ap.sports) ? ap.sports : []).join('; '),
        ap.position || '',
        ap.team || '',
        ap.class_year || '',
        ap.height_in || '',
        ap.weight_lbs || '',
        ap.location || '',
      ].join(',')
    })
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `roster-${new Date().toISOString().slice(0, 10)}.csv`; a.click()
    URL.revokeObjectURL(url)
  }, [roster])

  const handleLink = useCallback(async () => {
    if (!linkDraft) return
    setActionLoading(-1)
    await onLinkProspect(Number(linkDraft))
    setLinkDraft('')
    setActionLoading(null)
  }, [linkDraft, onLinkProspect])

  const handleNominate = useCallback(async () => {
    if (!nomName.trim() || !onNominateProspect) return
    setNomSubmitting(true)
    setNomMessage('')
    try {
      await onNominateProspect({ name: nomName.trim(), email: nomEmail.trim() || undefined, sport: nomSport.trim() || undefined })
      setNomMessage('Nominee added! Confirmation email sent.')
      setNomName(''); setNomEmail(''); setNomSport('')
      setTimeout(() => { setNomMessage(''); setShowNominate(false) }, 3000)
    } catch (err: any) {
      setNomMessage(err?.message || 'Failed to nominate')
    } finally {
      setNomSubmitting(false)
    }
  }, [nomName, nomEmail, nomSport, onNominateProspect])

  const handleUuidAdd = useCallback(async () => {
    if (!uuidInput.trim() || !onAddByUuid) return
    setUuidLoading(true)
    try {
      await onAddByUuid(uuidInput.trim())
      setUuidInput('')
      setShowUuid(false)
    } catch { /* ignore */ }
    finally { setUuidLoading(false) }
  }, [uuidInput, onAddByUuid])

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-stone-100/80 px-4 py-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="font-semibold text-stone-900">{roster.length} total</span>
          <span className="text-emerald-700">{activeCount} active/eligible</span>
        </div>
        <div className="ml-auto flex items-center gap-3 text-sm">
          <span className="text-stone-600">Budget: <strong>{formatCurrency(roster.filter((m) => m.active !== false).reduce((s, m) => s + (budgetPlans[m.prospect_id]?.planned_annual_allocation_cents ?? 0), 0))}</strong></span>
          <button type="button" onClick={exportCsv} className="flex items-center gap-1 rounded-lg border border-stone-300 px-2.5 py-1 text-xs font-medium text-stone-600 hover:bg-stone-200">
            <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Group manager */}
      <GroupManager
        groups={groups}
        activeGroupId={activeGroupId}
        onSelectGroup={setActiveGroupId}
        onCreate={onGroupCreate}
        onUpdate={onGroupUpdate}
        onDelete={onGroupDelete}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search athletes..." className="w-full rounded-xl border border-stone-200 py-2 pl-9 pr-3 text-sm text-stone-900 shadow-inner focus:border-stone-400 focus:outline-none" />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 focus:border-stone-400 focus:outline-none"
        >
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {/* Sport filter */}
        {allSports.length > 0 && (
          <select
            value={sportFilter}
            onChange={(e) => setSportFilter(e.target.value)}
            className="rounded-xl border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 focus:border-stone-400 focus:outline-none"
          >
            <option value="">All sports</option>
            {allSports.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-stone-700">{selected.size} selected:</span>
            <select value={bulkStatusValue} onChange={(e) => setBulkStatusValue(e.target.value)} className="rounded-lg border border-stone-200 px-2 py-1 text-xs">
              {STATUS_OPTIONS.filter((s) => s.value !== 'all').map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button type="button" onClick={() => { onBulkStatus(Array.from(selected), bulkStatusValue); setSelected(new Set()) }} className="rounded-lg bg-stone-900 px-2.5 py-1 font-semibold text-white">Set Status</button>
            {groups.length > 0 && (
              <>
                <select value={bulkGroupId} onChange={(e) => setBulkGroupId(e.target.value)} className="rounded-lg border border-stone-200 px-2 py-1 text-xs">
                  <option value="">Assign group...</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                {bulkGroupId && (
                  <button type="button" onClick={() => { onBulkGroupAssign(bulkGroupId, Array.from(selected)); setSelected(new Set()); setBulkGroupId('') }} className="rounded-lg bg-indigo-600 px-2.5 py-1 font-semibold text-white">Assign</button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Add athlete / Nominate / UUID */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-xl border border-dashed border-stone-300 bg-stone-50 px-4 py-3">
          <span className="text-xs text-stone-500 shrink-0">Add from roster:</span>
          <select className="flex-1 rounded-lg border border-stone-200 px-2 py-1.5 text-sm text-stone-900 focus:border-stone-400 focus:outline-none" value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)} disabled={!linkableProspects.length}>
            <option value="">Select a prospect</option>
            {linkableProspects.map((p) => <option key={p.id} value={p.id}>{p.name || p.headline || `#${p.id}`}</option>)}
          </select>
          <button type="button" onClick={handleLink} disabled={!linkDraft || actionLoading === -1} className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
            {actionLoading === -1 ? 'Adding...' : 'Add'}
          </button>
          <span className="text-stone-300">|</span>
          {onNominateProspect && (
            <button type="button" onClick={() => { setShowNominate(!showNominate); setShowUuid(false) }} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${showNominate ? 'bg-cocoa text-white' : 'border border-stone-300 text-stone-600 hover:bg-stone-100'}`}>
              + Nominate
            </button>
          )}
          {onAddByUuid && (
            <button type="button" onClick={() => { setShowUuid(!showUuid); setShowNominate(false) }} className="text-[10px] text-stone-400 hover:text-stone-600" title="Add by prospect ID">
              UUID
            </button>
          )}
        </div>

        {/* Nominate inline form */}
        {showNominate && onNominateProspect && (
          <div className="rounded-xl border border-cocoa/20 bg-cocoa/5 px-4 py-3 space-y-2">
            <p className="text-xs font-semibold text-cocoa">Nominate a new prospect for the draw pool</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <input className="rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-cocoa focus:outline-none" value={nomName} onChange={(e) => setNomName(e.target.value)} placeholder="Prospect name *" />
              <input type="email" className="rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-cocoa focus:outline-none" value={nomEmail} onChange={(e) => setNomEmail(e.target.value)} placeholder="Prospect email" />
              <input className="rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-cocoa focus:outline-none" value={nomSport} onChange={(e) => setNomSport(e.target.value)} placeholder="Sport" />
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={handleNominate} disabled={nomSubmitting || !nomName.trim()} className="rounded-lg bg-cocoa px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                {nomSubmitting ? 'Submitting...' : 'Nominate & Send Email'}
              </button>
              <button type="button" onClick={() => setShowNominate(false)} className="text-xs text-stone-400">Cancel</button>
              {nomMessage && <span className="text-xs text-cocoa/70">{nomMessage}</span>}
            </div>
          </div>
        )}

        {/* UUID add (subtle) */}
        {showUuid && onAddByUuid && (
          <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
            <span className="text-[10px] text-stone-400 shrink-0">Prospect ID:</span>
            <input className="flex-1 rounded border border-stone-200 px-2 py-1 font-mono text-xs text-stone-700 focus:border-stone-400 focus:outline-none" value={uuidInput} onChange={(e) => setUuidInput(e.target.value)} placeholder="Enter prospect UUID or numeric ID" />
            <button type="button" onClick={handleUuidAdd} disabled={uuidLoading || !uuidInput.trim()} className="rounded bg-stone-700 px-2.5 py-1 text-[10px] font-semibold text-white disabled:opacity-50">
              {uuidLoading ? '...' : 'Add'}
            </button>
            <button type="button" onClick={() => setShowUuid(false)} className="text-[10px] text-stone-400">×</button>
          </div>
        )}
      </div>

      {/* Loading */}
      {rosterLoading && <div className="py-6 text-center text-sm text-stone-500">Loading roster...</div>}

      {/* Table */}
      {!rosterLoading && filtered.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="min-w-full divide-y divide-stone-200 text-sm">
            <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5 rounded border-stone-300" /></th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('name')}>Athlete{sortInd('name')}</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('status')}>Status{sortInd('status')}</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('sport')}>Sports{sortInd('sport')}</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('team')}>Team{sortInd('team')}</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('classYear')}>Class{sortInd('classYear')}</th>
                <th className="px-3 py-2">Groups</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {filtered.map((member) => {
                const name = prospectName(member.prospect, member.prospect_id)
                const status = getMemberStatus(member)
                const statusMeta = getStatusMeta(status)
                const sports = getSports(member)
                const team = getTeam(member)
                const classYear = getClassYear(member)
                const memberGroups = (groupMemberships[member.prospect_id] || []).map((gid) => groups.find((g) => g.id === gid)).filter(Boolean)
                const isLoading = actionLoading === member.prospect_id

                return (
                  <tr key={member.prospect_id} className="hover:bg-stone-50">
                    <td className="px-3 py-3"><input type="checkbox" checked={selected.has(member.prospect_id)} onChange={() => toggleOne(member.prospect_id)} className="h-3.5 w-3.5 rounded border-stone-300" /></td>
                    <td className="px-3 py-3">
                      <button type="button" className="flex items-center gap-2 text-left hover:underline" onClick={() => setViewingMember(member)}>
                        {member.prospect?.avatar_url ? (
                          <img src={member.prospect.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-200 text-xs font-bold text-stone-600">{(name[0] || '?').toUpperCase()}</div>
                        )}
                        <span className="font-semibold text-stone-900">{name}</span>
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusMeta.color}`}>{statusMeta.label}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {sports.length > 0 ? sports.map((s) => (
                          <span key={s} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">{s}</span>
                        )) : <span className="text-stone-300">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-stone-700">{team || <span className="text-stone-300">—</span>}</td>
                    <td className="px-3 py-3 text-stone-700">{classYear || <span className="text-stone-300">—</span>}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {memberGroups.length > 0 ? memberGroups.map((g) => (
                          <span key={g!.id} className="rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ backgroundColor: g!.color || '#6b7280' }}>{g!.name}</span>
                        )) : <span className="text-stone-300">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button type="button" onClick={() => setViewingMember(member)} className="rounded-lg border border-stone-200 px-2 py-1 text-xs text-stone-600 hover:bg-stone-50">Edit</button>
                        <button type="button" onClick={async () => { if (!window.confirm(`Remove ${name}?`)) return; setActionLoading(member.prospect_id); await onRemoveMember(member); setActionLoading(null) }} disabled={isLoading} className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50">Remove</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty */}
      {!rosterLoading && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-stone-300 py-8 text-center text-sm text-stone-500">
          {roster.length === 0 ? 'No athletes linked yet. Add a prospect above.' : 'No athletes match your filters.'}
        </div>
      )}

      {/* Athlete slideout */}
      {viewingMember && (
        <AthleteSlideout
          member={viewingMember}
          budgetPlan={budgetPlans[viewingMember.prospect_id]}
          activity={activityByProspect[viewingMember.prospect_id]}
          groups={groups}
          athleteGroupIds={groupMemberships[viewingMember.prospect_id] || []}
          token={token}
          onClose={() => setViewingMember(null)}
          onSaved={onRefresh}
          onStatusChange={async (pid, s) => { await onStatusChange(pid, s); onRefresh() }}
          onGroupToggle={async (gid, pid, add) => { await onGroupToggle(gid, pid, add); onRefresh() }}
        />
      )}
    </div>
  )
}
